import React, { useState, useEffect } from "react";
import {
  Play,
  Pause,
  RotateCcw,
  Plus,
  X,
  FastForward,
  SkipForward,
  SkipBack,
  Sigma,
  Info,
} from "lucide-react";

// Helper functions to simulate Min/Max Heap operations on arrays
const pushToMaxHeap = (heap, val) => {
  heap.push(val);
  heap.sort((a, b) => a - b); // Keep sorted for easy access to max
};
const popFromMaxHeap = (heap) => {
  if (heap.length === 0) return undefined;
  return heap.pop();
};
const peekMaxHeap = (heap) => {
  if (heap.length === 0) return undefined;
  return heap[heap.length - 1];
};

const pushToMinHeap = (heap, val) => {
  heap.push(val);
  heap.sort((a, b) => a - b); // Keep sorted for easy access to min
};
const popFromMinHeap = (heap) => {
  if (heap.length === 0) return undefined;
  return heap.shift();
};
const peekMinHeap = (heap) => {
  if (heap.length === 0) return undefined;
  return heap[0];
};

const FindMedianVisualizer = () => {
  // Test cases
  const defaultTestCases = [
    {
      id: 1,
      name: "Example 1",
      stream: [1, 2, 3],
    },
    {
      id: 2,
      name: "Even Number of Elements",
      stream: [6, 10, 2, 6, 5, 0],
    },
    {
      id: 3,
      name: "Descending Order",
      stream: [10, 9, 8, 7, 6, 5],
    },
    {
      id: 4,
      name: "Single Element",
      stream: [5],
    },
    {
      id: 5,
      name: "Duplicates",
      stream: [2, 3, 3, 3, 2, 2],
    },
  ];

  const [testCases, setTestCases] = useState(defaultTestCases);
  const [selectedTestCase, setSelectedTestCase] = useState(0);
  const [customStream, setCustomStream] = useState("");
  const [stream, setStream] = useState(defaultTestCases[0].stream);
  const [showProblem, setShowProblem] = useState(true);

  // Visualization state
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1200);
  const [finished, setFinished] = useState(false);

  // Pseudo code
  const pseudoCode = [
    { line: "class MedianFinder:", indent: 0 },
    { line: "maxHeap = []  // Stores smaller half", indent: 1 },
    { line: "minHeap = []  // Stores larger half", indent: 1 },
    { line: "", indent: 0 },
    { line: "function addNum(num):", indent: 1 },
    { line: "maxHeap.push(num)", indent: 2 },
    { line: "// Balance heaps", indent: 2 },
    { line: "minHeap.push(maxHeap.pop())", indent: 2 },
    { line: "if minHeap.size > maxHeap.size:", indent: 2 },
    { line: "maxHeap.push(minHeap.pop())", indent: 3 },
    { line: "", indent: 0 },
    { line: "function findMedian():", indent: 1 },
    { line: "if maxHeap.size > minHeap.size:", indent: 2 },
    { line: "return maxHeap.peek()", indent: 3 },
    { line: "else:", indent: 2 },
    { line: "return (maxHeap.peek() + minHeap.peek()) / 2", indent: 3 },
  ];

  const [highlightedLine, setHighlightedLine] = useState(-1);

  // Calculate all steps
  const calculateSteps = (stream) => {
    const allSteps = [];
    let stepCount = 0;
    let maxHeap = []; // smaller half
    let minHeap = []; // larger half

    const addStep = (description, highlightLine, details = {}) => {
      allSteps.push({
        step: stepCount++,
        description,
        highlightLine,
        maxHeap: [...maxHeap],
        minHeap: [...minHeap],
        ...details,
      });
    };

    addStep("MedianFinder initialized.", 0, { median: null });

    for (let i = 0; i < stream.length; i++) {
      const num = stream[i];

      // Add number
      addStep(`Adding number: ${num}.`, 4, {
        currentNum: num,
        median: allSteps[allSteps.length - 1].median,
      });
      addStep(`Push ${num} to maxHeap (smaller half).`, 5, {
        currentNum: num,
        median: allSteps[allSteps.length - 1].median,
      });
      pushToMaxHeap(maxHeap, num);
      addStep(`maxHeap: [${maxHeap.join(", ")}]`, 5, {
        currentNum: num,
        median: allSteps[allSteps.length - 1].median,
      });

      // Balance
      addStep(`Balance: Move max element from maxHeap to minHeap.`, 7, {
        movingElement: peekMaxHeap(maxHeap),
        median: allSteps[allSteps.length - 1].median,
      });
      pushToMinHeap(minHeap, popFromMaxHeap(maxHeap));
      addStep(
        `Heaps after moving: maxHeap: [${maxHeap.join(
          ", "
        )}], minHeap: [${minHeap.join(", ")}]`,
        7,
        { median: allSteps[allSteps.length - 1].median }
      );

      if (minHeap.length > maxHeap.length) {
        addStep(
          `Balance: minHeap is larger. Move min element back to maxHeap.`,
          8,
          {
            movingElement: peekMinHeap(minHeap),
            median: allSteps[allSteps.length - 1].median,
          }
        );
        pushToMaxHeap(maxHeap, popFromMinHeap(minHeap));
        addStep(
          `Heaps rebalanced: maxHeap: [${maxHeap.join(
            ", "
          )}], minHeap: [${minHeap.join(", ")}]`,
          9,
          { median: allSteps[allSteps.length - 1].median }
        );
      }

      // Find Median
      let median;
      addStep(`Finding median...`, 11);
      if (maxHeap.length > minHeap.length) {
        median = peekMaxHeap(maxHeap);
        addStep(`maxHeap is larger. Median is its max element: ${median}`, 13, {
          median,
        });
      } else {
        median = (peekMaxHeap(maxHeap) + peekMinHeap(minHeap)) / 2;
        addStep(
          `Heaps are equal size. Median is average of max/min: (${peekMaxHeap(
            maxHeap
          )} + ${peekMinHeap(minHeap)}) / 2 = ${median}`,
          15,
          { median }
        );
      }
    }

    addStep("End of data stream.", -1, {
      median: allSteps[allSteps.length - 1].median,
      isFinal: true,
    });
    return allSteps;
  };

  useEffect(() => {
    const newSteps = calculateSteps(stream);
    setSteps(newSteps);
    setCurrentStep(0);
    setFinished(false);
  }, [stream]);

  useEffect(() => {
    if (!isPlaying || finished) return;
    const timer = setTimeout(() => handleNextStep(), speed);
    return () => clearTimeout(timer);
  }, [isPlaying, currentStep, finished, speed]);

  const handleNextStep = () => {
    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setHighlightedLine(steps[nextStep].highlightLine);
      if (steps[nextStep].isFinal) setFinished(true);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      setCurrentStep(prevStep);
      setHighlightedLine(steps[prevStep].highlightLine);
      setFinished(false);
    }
  };

  const reset = () => {
    setCurrentStep(0);
    setIsPlaying(false);
    setHighlightedLine(-1);
    setFinished(false);
  };

  const handleTestCaseChange = (index) => {
    setSelectedTestCase(index);
    setStream(testCases[index].stream);
  };

  const addCustomTestCase = () => {
    try {
      const parsedStream = JSON.parse(`[${customStream}]`);
      if (
        !Array.isArray(parsedStream) ||
        !parsedStream.every((n) => typeof n === "number")
      ) {
        throw new Error("Invalid stream format.");
      }
      const newTestCase = {
        id: Date.now(),
        name: `Custom`,
        stream: parsedStream,
      };
      setTestCases([...testCases, newTestCase]);
      setCustomStream("");
    } catch (error) {
      alert(
        `Invalid format. ${error.message} Use comma-separated numbers for the stream.`
      );
    }
  };

  const removeTestCase = (id) => {
    if (testCases.length > 1) {
      setTestCases(testCases.filter((tc) => tc.id !== id));
      if (selectedTestCase >= testCases.length - 1) setSelectedTestCase(0);
    }
  };

  const getSpeedLabel = () => {
    if (speed <= 600) return "Fast";
    if (speed <= 1200) return "Normal";
    return "Slow";
  };

  const currentStepData = steps[currentStep] || steps[0];

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <Sigma className="text-blue-500" size={24} /> Find Median from Data
        Stream
      </h2>

      {showProblem && (
        <div className="mb-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-start gap-2">
            <Info className="text-blue-600 mt-1" size={16} />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">
                Problem Statement
              </h3>
              <p className="text-sm text-blue-800">
                Implement a data structure that supports adding numbers from a
                stream and finding the median of all elements added so far. The
                median is the middle value in an ordered list, or the average of
                the two middle values if the list size is even.
              </p>
            </div>
            <button
              onClick={() => setShowProblem(false)}
              className="text-blue-600 hover:text-blue-800"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left Side */}
        <div className="space-y-3">
          <div className="bg-white p-3 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Test Cases</h3>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {testCases.map((tc, idx) => (
                <div key={tc.id} className="flex items-center gap-1">
                  <button
                    onClick={() => handleTestCaseChange(idx)}
                    className={`flex-1 px-2 py-1.5 text-left rounded transition-colors w-full ${
                      selectedTestCase === idx
                        ? "bg-blue-500 text-white"
                        : "bg-gray-100 hover:bg-gray-200"
                    }`}
                  >
                    <div className="text-xs font-medium">{tc.name}</div>
                    <div className="text-xs opacity-75">
                      Stream: [{tc.stream.join(", ")}]
                    </div>
                  </button>
                  {tc.id > 5 && (
                    <button
                      onClick={() => removeTestCase(tc.id)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={customStream}
                onChange={(e) => setCustomStream(e.target.value)}
                placeholder="Stream: 1,2,3"
                className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <button
                onClick={addCustomTestCase}
                className="w-full px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 flex items-center justify-center gap-1"
              >
                <Plus size={12} /> Add Test Case
              </button>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow min-h-[300px]">
            <h3 className="text-sm font-semibold mb-2">
              Step {currentStep}: {currentStepData?.description}
            </h3>

            <div className="grid grid-cols-2 gap-4 mt-4">
              {/* Max Heap */}
              <div className="text-center">
                <h4 className="font-semibold mb-2">Max Heap (Smaller Half)</h4>
                <div className="p-2 bg-red-50 border-2 border-red-200 rounded min-h-[100px] flex flex-col-reverse items-center gap-1">
                  {currentStepData?.maxHeap.map((val, idx) => (
                    <div
                      key={idx}
                      className={`w-10 h-10 flex items-center justify-center rounded font-mono font-bold text-lg text-red-800 transition-all duration-300 ${
                        peekMaxHeap(currentStepData.maxHeap) === val
                          ? "bg-red-300"
                          : "bg-red-200"
                      } ${
                        currentStepData.movingElement === val
                          ? "animate-pulse"
                          : ""
                      }`}
                    >
                      {val}
                    </div>
                  ))}
                </div>
              </div>

              {/* Min Heap */}
              <div className="text-center">
                <h4 className="font-semibold mb-2">Min Heap (Larger Half)</h4>
                <div className="p-2 bg-blue-50 border-2 border-blue-200 rounded min-h-[100px] flex flex-col items-center gap-1">
                  {currentStepData?.minHeap.map((val, idx) => (
                    <div
                      key={idx}
                      className={`w-10 h-10 flex items-center justify-center rounded font-mono font-bold text-lg text-blue-800 transition-all duration-300 ${
                        peekMinHeap(currentStepData.minHeap) === val
                          ? "bg-blue-300"
                          : "bg-blue-200"
                      } ${
                        currentStepData.movingElement === val
                          ? "animate-pulse"
                          : ""
                      }`}
                    >
                      {val}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-4 text-center">
              <h4 className="text-lg font-bold text-gray-800">
                Current Median:
                <span className="p-2 rounded-lg bg-green-200 text-green-900 ml-2">
                  {currentStepData?.median !== null &&
                  currentStepData?.median !== undefined
                    ? currentStepData.median.toFixed(1)
                    : "N/A"}
                </span>
              </h4>
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex items-center gap-2">
              <button
                onClick={handlePreviousStep}
                disabled={currentStep <= 0}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipBack size={14} />
              </button>
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                disabled={finished}
                className="px-3 py-1.5 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 flex items-center gap-1"
              >
                {isPlaying ? <Pause size={14} /> : <Play size={14} />}{" "}
                {isPlaying ? "Pause" : "Play"}
              </button>
              <button
                onClick={handleNextStep}
                disabled={currentStep >= steps.length - 1}
                className="p-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300"
              >
                <SkipForward size={14} />
              </button>
              <button
                onClick={reset}
                className="px-3 py-1.5 text-xs bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <div className="flex items-center gap-1 ml-auto">
                <FastForward size={14} className="text-gray-600" />
                <label className="text-xs text-gray-600">Speed:</label>
                <input
                  type="range"
                  min="300"
                  max="2000"
                  step="100"
                  value={2300 - speed}
                  onChange={(e) => setSpeed(2300 - parseInt(e.target.value))}
                  className="w-20"
                />
                <span className="text-xs font-medium w-10">
                  {getSpeedLabel()}
                </span>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-center gap-0.5">
              {steps.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentStep
                      ? "bg-blue-500 w-6"
                      : idx < currentStep
                      ? "bg-blue-300"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Right Side */}
        <div className="space-y-3">
          <div className="bg-gray-900 text-gray-100 p-4 rounded-lg shadow font-mono text-xs">
            <h3 className="text-sm font-semibold mb-3 text-white">
              Two Heaps Algorithm
            </h3>
            <div className="space-y-0.5">
              {pseudoCode.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-0.5 px-2 rounded transition-all duration-300 ${
                    idx === highlightedLine
                      ? "bg-yellow-500 bg-opacity-30 text-yellow-200"
                      : ""
                  }`}
                  style={{ paddingLeft: `${line.indent * 16 + 8}px` }}
                >
                  {line.line}
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">
              Complexity & Strategy
            </h3>
            <p className="text-xs text-gray-600 mb-2">
              The optimal solution uses two heaps: a Max Heap to store the
              smaller half of the numbers and a Min Heap for the larger half.
              This keeps the data stream divided into two halves, with the
              median always available at the top of the heaps. The heaps are
              kept balanced so their sizes differ by at most one.
            </p>
            <div className="flex justify-around mt-1 text-xs">
              <div>
                <span className="font-medium">Add Time:</span> O(log N)
              </div>
              <div>
                <span className="font-medium">Median Time:</span> O(1)
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              N = number of elements in the stream.
            </p>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2 text-blue-900">
              Key Insights
            </h3>
            <ul className="space-y-1 text-xs text-blue-800">
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  The Max Heap's largest element is always less than or equal to
                  the Min Heap's smallest element.
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  By keeping the heaps balanced, the median is either the top of
                  the larger heap (odd count) or the average of the tops of both
                  heaps (even count).
                </span>
              </li>
              <li className="flex">
                <span className="mr-1">💡</span>
                <span>
                  A simple balancing rule: always add to one heap, then move the
                  top to the other. If this unbalances them, move one back. This
                  keeps the logic simple.
                </span>
              </li>
            </ul>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-semibold mb-2">Visual Guide</h3>
            <div className="space-y-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded bg-red-300 text-center flex items-center justify-center font-bold">
                  ...
                </div>
                <span>Top of Max Heap (Largest of small half)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded bg-blue-300 text-center flex items-center justify-center font-bold">
                  ...
                </div>
                <span>Top of Min Heap (Smallest of large half)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded bg-red-200 animate-pulse"></div>
                <span>Element being moved between heaps</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FindMedianVisualizer;
